/* eslint-disable react-hooks/exhaustive-deps */
import SettingsPanel from './components/settings-panel/SettingsPanel';
import SettingsToggle from './components/settings-panel/SettingsToggle';
import useToggleStyle from './hooks/useToggleStyle';
import { useAppContext } from './providers/AppProvider';
import { useSettingsPanelContext } from './providers/SettingsPanelProvider';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import RoleDeniedModal from './components/modals/RoleDeniedModal';
import axios from 'axios';

// Did the request that came back 401 send credentials at all?
// axios 1.x hands back an AxiosHeaders instance on err.config.headers, which
// answers .get(); older shapes are a plain object, hence both lookups.
const requestHadToken = err => {
  const headers = err?.config?.headers;
  if (!headers) return false;
  const read = name =>
    typeof headers.get === 'function' ? headers.get(name) : headers[name];
  return Boolean(read('x-auth-token') || read('Authorization') || read('authorization'));
};

const App = () => {
  const { isStylesheetLoaded } = useToggleStyle();
  const { pathname } = useLocation();

  const {
    settingsPanelConfig: { showSettingPanelButton },
    setSettingsPanelConfig
  } = useSettingsPanelContext();

  const {
    config: { theme, isRTL }
  } = useAppContext();

  // Automatically scrolls to top whenever pathname changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Global 401 interceptor — redirect to login when token expires.
  //
  // Only when the failed request actually CARRIED a token. A 401 on a request
  // that went out without one says nothing about the session being invalid —
  // it says that one call was made too early or forgot the header — and
  // treating it as an expiry logs the user out of a session that was perfectly
  // good. That is how a single mis-ordered request on the login path turned
  // into "the dashboard flashes and bounces back to the login screen", with
  // nothing left in the console to explain it, because the reload cleared it.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      res => res,
      err => {
        if (err?.response?.status === 401 && requestHadToken(err)) {
          localStorage.removeItem('token');
          window.location.href = '/';
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    setSettingsPanelConfig({
      openSettingPanel: false
    });
  }, [isRTL]);

  return (
    <>
      {!isStylesheetLoaded ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: theme === 'dark' ? '#000' : '#fff'
          }}
        />
      ) : (
        <>
          <Outlet />
          {/* Every page can now be opened by every role, so the refusal has to
              be able to appear over any of them. Mounted here, once. */}
          <RoleDeniedModal />
          {showSettingPanelButton && (
            <>
              <SettingsToggle />
              <SettingsPanel />
            </>
          )}
        </>
      )}
    </>
  );
};

export default App;
